import { useEffect, useState } from 'react';
import { ErrorOutline } from '@mui/icons-material';
import { Box, Button, Card, Link, Stack, Typography } from '@mui/material';

interface ErrorBoundaryFallbackProps {
  error: Error;
  resetError: () => void;
}

export const ErrorBoundaryFallback = (props: ErrorBoundaryFallbackProps) => {
  const { resetError } = props;
  const [currentLocation] = useState(window.location.href);

  useEffect(() => {
    const locationChange = () => {
      if (window.location.href !== currentLocation) {
        resetError();
      }
    };
    window.addEventListener('popstate', locationChange);
    return () => {
      window.removeEventListener('popstate', locationChange);
    };
  }, [currentLocation, resetError]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        p: 3
      }}
    >
      <Card
        sx={{
          width: '100%',
          maxWidth: 896,
          minHeight: 508,
          borderRadius: 3,
          bgcolor: 'background.default'
        }}
      >
        <Stack
          alignItems="center"
          sx={{
            minHeight: 'inherit',
            boxSizing: 'border-box',
            px: { xs: 3, sm: 8 },
            py: 8
          }}
        >
          <Box
            sx={{
              width: 104,
              height: 104,
              display: 'grid',
              placeItems: 'center',
              borderRadius: '50%',
              bgcolor: 'error.surface'
            }}
          >
            <ErrorOutline sx={{ fontSize: 56, color: 'error.main' }} />
          </Box>

          <Typography variant="h5" fontWeight={700} sx={{ mt: 3 }}>
            We're experiencing an issue!
          </Typography>

          <Typography
            color="text.primary"
            textAlign="center"
            sx={{ maxWidth: 650, mt: 1, fontSize: '1.25rem' }}
          >
            Our team has been notified and is actively investigating. Please try
            reloading the page.
          </Typography>

          <Button
            variant="contained"
            onClick={() => window.location.reload()}
            sx={{ mt: 3 }}
          >
            Reload Page
          </Button>

          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            sx={{ mt: 'auto', pt: 6 }}
          >
            If the issue persists, contact our support team at{' '}
            <Link href="mailto:help@exacare.com">help@exacare.com</Link> for
            assistance.
          </Typography>
        </Stack>
      </Card>
    </Box>
  );
};
